export type ResultItem = {
  id: string
  name: string
  subtitle?: string
  url?: string
  createdAt?: string
  level?: number
  score?: number
  imageScore?: number
  textScore?: number
  embedSupported?: boolean
  category?: string
}

// Minimal shape needed by flattenTree — satisfied by Document without importing its full entity chain.
export type FlattenableDoc = {
  id: string
  name: string
  topic?: string
  subtitle?: string
  createdAt: string
  published: boolean
  children?: FlattenableDoc[]
  bodySimilarity?: number
  nameSimilarity?: number
}

export type TopicIndex = {
  childCounts: Record<string, number>
  itemParentId: Record<string, string>
}

export function getDateStart(dateId: string): Date | undefined {
  const days: Record<string, number> = { '1': 7, '2': 30, '3': 183 }
  if (!days[dateId]) return undefined
  const d = new Date()
  d.setDate(d.getDate() - days[dateId])
  return d
}

export function compareItems(
  a: ResultItem,
  b: ResultItem,
  sortId: string,
): number {
  switch (sortId) {
    case '1':
      return (b.createdAt ?? '').localeCompare(a.createdAt ?? '')
    case '2':
      return (a.createdAt ?? '').localeCompare(b.createdAt ?? '')
    case '3':
      return a.name.localeCompare(b.name)
    case '4':
      return b.name.localeCompare(a.name)
    default:
      return (b.score ?? 0) - (a.score ?? 0)
  }
}

export function sortResultItems(
  items: ResultItem[],
  sortId: string,
): ResultItem[] {
  return [...items].sort((a, b) => compareItems(a, b, sortId))
}

// Groups a flat level-annotated array into [parent, ...children] segments.
export function segmentFlatTree(items: ResultItem[]): ResultItem[][] {
  const segments: ResultItem[][] = []
  for (const item of items) {
    if (!item.level) segments.push([item])
    else if (segments.length) segments[segments.length - 1].push(item)
  }
  return segments
}

// Returns per-parent child counts and a child→parent lookup, derived from segmentFlatTree.
export function buildTopicIndex(items: ResultItem[]): TopicIndex {
  const childCounts: Record<string, number> = {}
  const itemParentId: Record<string, string> = {}
  for (const segment of segmentFlatTree(items)) {
    const parent = segment[0]
    childCounts[parent.id] = segment.length - 1
    for (const child of segment.slice(1)) {
      itemParentId[child.id] = parent.id
    }
  }
  return { childCounts, itemParentId }
}

export function sortFlatTree(
  items: ResultItem[],
  sortId: string,
): ResultItem[] {
  if (!items.length) return items
  const segments = segmentFlatTree(items)
  segments.sort((a, b) => compareItems(a[0], b[0], sortId))
  return segments.flat()
}

// Flattens a topic family to 1 level deep, making the newest doc the displayed parent
// and sorting remaining versions as level-1 children (newest first) — matches /topics page behaviour.
export function flattenTree(doc: FlattenableDoc): ResultItem[] {
  const publishUrl = process.env.REACT_APP_PUBLISH_URL
  const previewUrl = process.env.REACT_APP_PREVIEW_URL
  const makeItem = (d: FlattenableDoc, level: number): ResultItem => ({
    id: d.id,
    name: d.topic || d.name,
    subtitle: d.subtitle,
    createdAt: d.createdAt,
    level,
    url: d.published
      ? `https://${publishUrl}/lx/${d.id}/index.html`
      : `https://${previewUrl}/${d.id}/index.html`,
    score:
      d.bodySimilarity != null && d.nameSimilarity != null
        ? d.bodySimilarity ** 2 + d.nameSimilarity ** 2
        : undefined,
    embedSupported: true,
  })
  const family = [doc, ...(doc.children ?? [])]
  family.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  )
  return [makeItem(family[0], 0), ...family.slice(1).map((d) => makeItem(d, 1))]
}

export function getArchUrl(
  clientId: string | null | undefined,
  programId: string | null | undefined,
): string | undefined {
  return clientId && programId
    ? `/architectures/${clientId}?programId=${programId}`
    : undefined
}
